import http from './http';

export interface Conversation {
  id: string;
  title: string;
  archived?: boolean;
  session_kind?: 'isolated' | string;
  updated_at?: string;
  created_at?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at?: string;
  provider?: string;
  model?: string;
  credential_source?: 'personal' | 'workspace' | 'system' | string;
  input_tokens?: number;
  output_tokens?: number;
  cost_usd?: number;
  latency_ms?: number;
  status?: string;
  usage?: { input_tokens?: number; output_tokens?: number; cost_usd?: number; credential_source?: string } | null;
  attachments?: Array<{ id: string; asset_id: string; filename: string; mime_type?: string; file_size?: number }>;
}

export interface SendMessageInput {
  content: string;
  provider?: string;
  model?: string;
  credential_profile_id?: string;
  allow_paid_fallback?: boolean;
  attachment_ids?: string[];
  use_shared_memory?: boolean;
  /** Which surface is talking: 'chat' (default) or 'widget'. Only the floating
   *  widget may drive screen navigation (owner report 2026-09-30). */
  source?: 'chat' | 'widget' | string;
  /** ANSWERED-CALL TURN (owner spec 2026-10-13): set by the widget when the
   *  owner just answered a signed agent call — the backend seeds the model/
   *  bridge prompt with the call reason ("Deliver it now, concisely"). */
  answered_call_request_id?: string;
}

export interface RegenerateInput {
  content?: string;
  provider?: string;
  model?: string;
  credential_profile_id?: string;
  allow_paid_fallback?: boolean;
  use_shared_memory?: boolean;
  source?: 'chat' | 'widget' | string;
}

interface SSEHandlers {
  onStart?: (message: ChatMessage) => void;
  onDelta: (delta: string) => void;
  onDone: (message: ChatMessage) => void;
  onStopped?: () => void;
}

const list = <T>(data: T[] | { items?: T[]; conversations?: T[]; messages?: T[] }): T[] =>
  Array.isArray(data) ? data : data.items || data.conversations || data.messages || [];

async function streamSSE<T extends object>(url: string, input: T, handlers: SSEHandlers): Promise<void> {
  const token = localStorage.getItem('saik_token');
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    let detail = `Chat request failed (${response.status})`;
    try { detail = (await response.json()).detail || detail; } catch { /* non-JSON error */ }
    throw new Error(detail);
  }
  if (!response.body) throw new Error('Streaming is unavailable in this browser.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const dispatch = (block: string) => {
    let event = 'message'; let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (!data) return;
    const payload = JSON.parse(data);
    if (event === 'start') handlers.onStart?.(payload.user_message);
    else if (event === 'delta') handlers.onDelta(payload.delta || '');
    else if (event === 'done') handlers.onDone(payload.assistant_message);
    else if (event === 'stopped') handlers.onStopped?.();
    else if (event === 'error') throw new Error(payload.detail || 'AI response failed');
  };
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n');
    const blocks = buffer.split('\n\n'); buffer = blocks.pop() || '';
    blocks.forEach(dispatch);
    if (done) { if (buffer.trim()) dispatch(buffer); break; }
  }
}

export const chatApi = {
  async conversations(workspaceId: string, archived = false) {
    const response = await http.get<Conversation[] | { items?: Conversation[]; conversations?: Conversation[] }>('/chat/conversations', { params: { workspace_id: workspaceId, archived } });
    return list(response.data);
  },
  async createConversation(input: { workspace_id: string; title?: string; binding?: string; provider?: string; model?: string }) {
    const response = await http.post<Conversation>('/chat/conversations', input);
    return response.data;
  },
  async messages(conversationId: string) {
    const response = await http.get<ChatMessage[] | { items?: ChatMessage[]; messages?: ChatMessage[] }>(`/chat/conversations/${conversationId}/messages`);
    return list(response.data);
  },
  async send(conversationId: string, input: SendMessageInput) {
    const response = await http.post<ChatMessage | { message?: ChatMessage; assistant_message?: ChatMessage }>(`/chat/conversations/${conversationId}/messages`, input);
    if ('assistant_message' in response.data && response.data.assistant_message) return response.data.assistant_message;
    return 'message' in response.data && response.data.message ? response.data.message : response.data as ChatMessage;
  },
  stream: (conversationId: string, input: SendMessageInput, handlers: SSEHandlers) =>
    streamSSE(`/api/v1/chat/conversations/${conversationId}/messages/stream`, input, handlers),
  regenerate: (conversationId: string, input: RegenerateInput) =>
    http.post<ChatMessage>(`/chat/conversations/${conversationId}/regenerate`, input).then(r => r.data),
  regenerateStream: (conversationId: string, input: RegenerateInput, handlers: SSEHandlers) =>
    streamSSE(`/api/v1/chat/conversations/${conversationId}/regenerate/stream`, input, handlers),
  rename: (id: string, title: string) => http.patch<Conversation>(`/chat/conversations/${id}`, { title }),
  archive: (id: string) => http.patch<Conversation>(`/chat/conversations/${id}`, { archived: true }),
  stop: (id: string) => http.post(`/chat/conversations/${id}/stop`),
  dailyReset: (workspaceId: string) =>
    http.post<Conversation>('/chat/conversations/daily-reset', null, {
      params: { workspace_id: workspaceId, tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
    }).then(r => r.data),
  openclawConversation: (workspaceId: string) =>
    http.post<Conversation>('/chat/conversations/openclaw', null, {
      params: { workspace_id: workspaceId },
    }).then(r => r.data),
  macConversation: (workspaceId: string) =>
    http.post<Conversation>('/chat/conversations/mac', null, {
      params: { workspace_id: workspaceId },
    }).then(r => r.data),
  /** Real OpenClaw agent busy state for a chat line (ricardo | henry). */
  agentStatus: (line: 'ricardo' | 'henry') =>
    http.get<AgentStatus>(`/chat/agent-status`, { params: { line } }).then(r => r.data),
};

export interface AgentStatus {
  line: string;
  label: string | null;
  session_kind: string | null;
  session_key: string | null;
  status: string | null;
  busy: boolean;
  available: boolean;
  aborted_last_run?: boolean;
  started_at?: string | null;
  ended_at?: string | null;
  updated_at?: string | null;
}
